import { useListSSOConfigurations, useOrganizationInvite } from "@/queries/organization-queries";
import { BuiltinSSOConfigCard } from "@/routes/settings/login/SSOConfigCard";
import { CustomDomainCard } from "@/routes/settings/login/CustomDomainCard";
import { EnforceCustomDomainCard } from "@/routes/settings/login/EnforceCustomDomainCard";
import { CustomSSOSection } from "@/routes/settings/login/CustomSSOSection";
import { SCIMSection } from "@/routes/settings/login/SCIMSection";
import { SCIMOperationsLog } from "@/routes/settings/login/SCIMOperationsLog";
import {
    SSOConfiguration_ProviderType,
    SSOConfigurationState,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { type FC, useMemo, useState } from "react";
import { Banner } from "@/components/flexkit/Banner";
import { Link } from "@/components/flexkit/Link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";
import { IconLoginSecurityConfig } from "@/assets/icons/IconLoginSecurityConfig";
import { IconOperationsLog } from "@/assets/icons/IconOperationsLog";
import { useFlagSCIMEnabled } from "@/hooks/use-flag";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Text } from "@/components/podkit/typography/Text";
import { ErrorMessage } from "@/components/ErrorMessage";

export const LoginAndSecurity: FC<{ organizationId: string; isEnterpriseTier?: boolean }> = ({
    organizationId,
    isEnterpriseTier = false,
}) => {
    const { value: isSCIMEnabled } = useFlagSCIMEnabled();
    const [currentTab, setCurrentTab] = useState<"configuration" | "operations">("configuration");

    return (
        <div className="flex flex-col gap-6">
            <div className="text-base text-content-secondary">Configure SSO for your organization.</div>

            {!isEnterpriseTier && (
                <Banner
                    variant="info"
                    className="py-4"
                    data-testid="free-tier-banner"
                    text={
                        <>
                            Upgrade to{" "}
                            <Link
                                to="/settings/manage-organization"
                                className="font-medium text-content-brand hover:underline"
                            >
                                Enterprise tier
                            </Link>{" "}
                            to manage login and security settings.
                        </>
                    }
                    action={{
                        text: "Upgrade now",
                        onClick: () => (window.location.href = "/settings/manage-organization"),
                        "data-tracking-id": "upgrade-enterprise-login-security",
                    }}
                />
            )}
            {isSCIMEnabled ? (
                <Tabs
                    value={currentTab}
                    onValueChange={(value) => setCurrentTab(value as "configuration" | "operations")}
                    className="flex w-full flex-col gap-6"
                >
                    <TabsList className="w-fit">
                        <TabsTrigger value="configuration" LeadingIcon={IconLoginSecurityConfig}>
                            Configuration
                        </TabsTrigger>
                        <TabsTrigger value="operations" LeadingIcon={IconOperationsLog}>
                            Operations Logs
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="configuration" className="flex w-full flex-col gap-6">
                        <div className="flex w-full max-w-[550px] flex-col gap-6">
                            <OrganizationLoginLink />
                            <DomainAndAccess organizationId={organizationId} editable={isEnterpriseTier} />
                            <LoginMethods organizationId={organizationId} editable={isEnterpriseTier} />
                            <SCIMSection organizationId={organizationId} editable={isEnterpriseTier} />
                        </div>
                    </TabsContent>
                    <TabsContent value="operations" className="flex w-full flex-col gap-6">
                        <SCIMOperationsLog />
                    </TabsContent>
                </Tabs>
            ) : (
                <>
                    <OrganizationLoginLink />
                    <DomainAndAccess organizationId={organizationId} editable={isEnterpriseTier} />
                    <LoginMethods organizationId={organizationId} editable={isEnterpriseTier} />
                </>
            )}
        </div>
    );
};

const DomainAndAccess: FC<{ organizationId: string; editable: boolean }> = ({ organizationId, editable }) => {
    return (
        <div className="flex flex-col gap-4">
            <Heading3>Custom Domain</Heading3>
            <CustomDomainCard organizationId={organizationId} editable={editable} />
            <EnforceCustomDomainCard organizationId={organizationId} editable={editable} />
        </div>
    );
};

const LoginMethods: FC<{ organizationId: string; editable: boolean }> = ({ organizationId, editable }) => {
    const { data: ssoConfigs } = useListSSOConfigurations();
    const builtinConfigs = useMemo(() => ssoConfigs?.filter(isBuiltin) || [], [ssoConfigs]);
    const customConfigs = useMemo(
        () =>
            ssoConfigs?.filter(isCustom).sort((a, b) => {
                const nameComparison = a.displayName.localeCompare(b.displayName);
                if (nameComparison !== 0) return nameComparison;
                return a.issuerUrl.localeCompare(b.issuerUrl);
            }) || [],
        [ssoConfigs],
    );
    const hasSeveralActive =
        (ssoConfigs?.filter((c) => c.state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE)?.length || 0) > 1;
    // Deactivation is allowed for all tiers when there are multiple active providers
    const deactivateable = hasSeveralActive;

    return (
        <div className="flex flex-col gap-4">
            <Heading3>Login Methods</Heading3>
            {builtinConfigs.map((config) => (
                <BuiltinSSOConfigCard
                    key={`built-in-config-${config.issuerUrl}`}
                    organizationId={organizationId}
                    config={config}
                    deactivateable={deactivateable}
                    editable={editable}
                />
            ))}
            <CustomSSOSection
                className="mt-4"
                organizationId={organizationId}
                configs={customConfigs}
                deactivateable={deactivateable}
                editable={editable}
            />
        </div>
    );
};

const OrganizationLoginLink: FC = () => {
    const { data: invite, isPending, error } = useOrganizationInvite();

    const loginURL = useMemo(() => {
        if (!invite?.inviteId) return "";
        return `${window.location.origin}/login?inviteId=${encodeURIComponent(invite.inviteId)}`;
    }, [invite?.inviteId]);

    const title = "Organization Login Link";

    if (isPending) {
        return (
            <div className="flex flex-col gap-4">
                <Heading3>{title}</Heading3>
                <SkeletonBlock ready={false} className="h-10 w-full" />
            </div>
        );
    }

    if (error || !invite?.inviteId) {
        return (
            <div className="flex flex-col gap-4">
                <Heading3>{title}</Heading3>
                <ErrorMessage error={error} />
                {!error && (
                    <Text className="text-base text-content-secondary">
                        Unable to load organization login link. Please try refreshing the page.
                    </Text>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <Heading3>{title}</Heading3>
            <Text className="text-base text-content-secondary">
                Share this link with members to login using supported methods.
            </Text>
            <CopyableInput value={loginURL} copyable disabled data-tracking-id="organization-login-link" />
        </div>
    );
};

const isCustom = (config: SSOConfiguration) => config.providerType === SSOConfiguration_ProviderType.CUSTOM;
const isBuiltin = (config: SSOConfiguration) => config.providerType === SSOConfiguration_ProviderType.BUILTIN;
